import { useMutation } from '@apollo/client/react'
import { isDocumentTag, isSenderTag } from '@getcaya/document-ingestion-util'
import { message } from 'antd'
import {
  type NatifExtractionErrorTagInput,
  ReportNatifExtractionErrorsDocument,
} from '~app/graph/generated/graphql'
import i18n from '~app/i18n/i18n'

/**
 * Custom hook for setting up the API call to report document extraction errors to Natif
 */
const useReportNatifTagExtractionErrors = ({
  documentId,
}: {
  documentId: string
}): {
  reportNatifTagExtractionErrors: (tags: string[]) => Promise<void>
  loading: boolean
} => {
  const [reportNatifExtractionErrorsMutation, { loading }] = useMutation(
    ReportNatifExtractionErrorsDocument
  )

  const reportNatifTagExtractionErrors = async (tags: string[]) => {
    try {
      const payload: Partial<NatifExtractionErrorTagInput> = {}
      tags.forEach(tag => {
        if (isSenderTag(tag)) {
          payload.senderTag = tag
        } else if (isDocumentTag(tag)) {
          payload.documentTag = tag
        }
      })
      if (!payload.documentTag || !payload.senderTag) {
        throw new Error(
          'Need both a document (what) and sender (who) tag selected.'
        )
      }
      await reportNatifExtractionErrorsMutation({
        variables: {
          data: {
            errorFields: {
              tags: payload as NatifExtractionErrorTagInput,
            },
          },
          where: {
            id: documentId,
          },
        },
      })
      message.success(i18n.t('natifErrorReport.successMessage'), 2.5)
    } catch (error) {
      message.error(i18n.t('natifErrorReport.errorMessage', { error }), 5)
      console.error(error)
    }
  }

  return {
    reportNatifTagExtractionErrors,
    loading,
  }
}

export { useReportNatifTagExtractionErrors }
